import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

import AppButton from "../AppButton";
import { getUserSettings, saveUserSettings } from "../../../actions/settings";
import {
  PRINT_APP_DOWNLOAD_URL,
  PRINT_APP_HELP_URL,
  PRINT_APP_INTEGRATIONS_PATH,
  dismissPrintAppInstallPrompt,
  isPrintAppInstallDismissed,
  registerPrintAppInstallPrompt,
  setEnterprisePrintAppInstallDismissed,
} from "../../../helpers/printApp";

const styles = {
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "20px 20px 0 20px",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#2563eb",
  },
  title: {
    margin: 0,
    fontSize: 17,
    fontWeight: 600,
    color: "#111",
    lineHeight: 1.3,
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#555",
  },
  body: {
    padding: "12px 20px 0",
    fontSize: 14,
    color: "#374151",
    lineHeight: 1.5,
  },
  checkboxRow: {
    padding: "8px 20px 0",
  },
  helpLink: {
    fontSize: 13,
    color: "#2563eb",
  },
};

/**
 * Global host for the Direct Print install prompt. Mount once in the app
 * layout; `openPrintApp()` opens it when the desktop app isn't detected.
 */
const persistEnterpriseDismiss = (dispatch) => {
  dismissPrintAppInstallPrompt();
  setEnterprisePrintAppInstallDismissed(true);
  dispatch(
    saveUserSettings({
      settings: { hideDirectPrintInstallPrompt: true },
    }),
  );
};

const PrintAppInstallDialog = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  useEffect(() => {
    dispatch(getUserSettings()).then((res) => {
      if (res?.payload?.data?.hideDirectPrintInstallPrompt) {
        setEnterprisePrintAppInstallDismissed(true);
      }
    });
  }, [dispatch]);

  useEffect(() => {
    return registerPrintAppInstallPrompt(() => {
      if (isPrintAppInstallDismissed()) return;
      setNeverShowAgain(isPrintAppInstallDismissed());
      setOpen(true);
    });
  }, []);

  const handleNeverShowChange = (checked) => {
    setNeverShowAgain(checked);
    // Persist immediately on the enterprise so every user / computer
    // stops seeing this after one "Don't show this again".
    if (checked) {
      persistEnterpriseDismiss(dispatch);
    }
  };

  const handleClose = () => {
    if (neverShowAgain) {
      persistEnterpriseDismiss(dispatch);
    }
    setOpen(false);
  };

  const handleInstall = () => {
    if (neverShowAgain) {
      persistEnterpriseDismiss(dispatch);
    }
    window.open(PRINT_APP_DOWNLOAD_URL, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.iconWrap}>
            <PrintOutlinedIcon fontSize="small" />
          </div>
          <div>
            <h2 style={styles.title}>Install the Direct Print app?</h2>
            <p style={styles.subtitle}>
              Shipping succeeded. We just couldn&apos;t reach the print app.
            </p>
          </div>
        </div>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent style={styles.body}>
        <p style={{ margin: "0 0 8px" }}>
          The Direct Print app prints shipping labels and packing slips
          automatically. Install it on this computer to print right away after
          shipping.
        </p>
        <p style={{ margin: 0 }}>
          <a
            href={PRINT_APP_HELP_URL}
            target="_blank"
            rel="noreferrer"
            style={styles.helpLink}
          >
            Learn more
          </a>
          {" · "}
          <RouterLink
            to={PRINT_APP_INTEGRATIONS_PATH}
            style={styles.helpLink}
            onClick={handleClose}
          >
            Integrations
          </RouterLink>
        </p>
      </DialogContent>

      <div style={styles.checkboxRow}>
        <FormControlLabel
          control={
            <Checkbox
              checked={neverShowAgain}
              onChange={(e) => handleNeverShowChange(e.target.checked)}
              size="small"
            />
          }
          label="Don't show this again"
          sx={{
            marginLeft: 0,
            "& .MuiFormControlLabel-label": { fontSize: 13, color: "#4b5563" },
          }}
        />
      </div>

      <DialogActions sx={{ padding: "12px 20px 20px", gap: 1 }}>
        <AppButton
          outline
          size="md"
          onClick={handleClose}
          color="#fff"
          textColor="#374151"
          borderColor="#d1d5db"
        >
          Not now
        </AppButton>
        <AppButton size="md" onClick={handleInstall}>
          Install app
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default PrintAppInstallDialog;
